import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, differenceInDays, startOfMonth, endOfMonth } from 'date-fns';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import { parseLocalDate, formatDisplayDate } from '../../utils/dateUtils';
import Button from '../../components/ui/Button';
import { projectService } from '../../services/projectService';
import { deliverableService } from '../../services/deliverableService';
import { supabase } from '../../lib/supabase';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return formatDisplayDate(d);
};

const formatCurrency = (val) => {
  if (!val && val !== 0) return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return '—';
  if (num >= 1000000) return `$${(num / 1000000)?.toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000)?.toFixed(0)}K`;
  return `$${num?.toLocaleString()}`;
};

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  completed: { label: 'Completed', cls: 'bg-green-100 text-green-700 border-green-200' },
  blocked: { label: 'Blocked', cls: 'bg-red-100 text-red-700 border-red-200' },
  on_hold: { label: 'On Hold', cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  bidding: { label: 'Bidding', cls: 'bg-violet-100 text-violet-700 border-violet-200' },
};

const getStatusConfig = (status) => {
  const key = (status || '')?.toLowerCase()?.replace(/[\s-]/g, '_');
  return STATUS_CONFIG?.[key] || { label: status || '—', cls: 'bg-gray-100 text-gray-500' };
};

const ROW_TYPE_CONFIG = {
  phase: { label: 'Phase', icon: 'GitBranch', barColor: 'bg-amber-500', badgeCls: 'bg-amber-100 text-amber-700' },
  deliverable: { label: 'Deliverable', icon: 'Package', barColor: 'bg-purple-500', badgeCls: 'bg-purple-100 text-purple-700' },
  task: { label: 'Task', icon: 'CheckSquare', barColor: 'bg-blue-500', badgeCls: 'bg-blue-100 text-blue-700' },
  subtask: { label: 'Sub-task', icon: 'CornerDownRight', barColor: 'bg-emerald-500', badgeCls: 'bg-emerald-100 text-emerald-700' },
};

// ─── Project Progress Bar ─────────────────────────────────────────────────────

const ProjectProgressBar = ({ progress }) => {
  const pct = Math.min(100, Math.max(0, parseFloat(progress) || 0));
  const color = pct >= 100 ? 'bg-green-500' : pct >= 60 ? 'bg-blue-500' : pct >= 30 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden flex-shrink-0">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">{pct?.toFixed(0)}%</span>
    </div>
  );
};

// ─── Inline Edit Cell ─────────────────────────────────────────────────────────

const EditableCell = ({ value, onSave, type = 'text', options = [], className = '', renderDisplay }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setVal(value || ''); }, [value]);
  useEffect(() => { if (editing && inputRef?.current) inputRef?.current?.focus(); }, [editing]);

  const handleSave = async () => {
    if (val === value) { setEditing(false); return; }
    setSaving(true);
    await onSave(val);
    setSaving(false);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') handleSave();
    if (e?.key === 'Escape') { setVal(value || ''); setEditing(false); }
  };

  if (!editing) {
    return (
      <span
        className={`cursor-pointer hover:bg-accent/30 rounded px-1 py-0.5 transition-colors ${className}`}
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {renderDisplay ? renderDisplay(value) : (value || <span className="text-muted-foreground italic text-xs">—</span>)}
      </span>
    );
  }

  if (type === 'select') {
    return (
      <select
        ref={inputRef}
        value={val}
        onChange={e => setVal(e?.target?.value)}
        onBlur={handleSave}
        className="text-xs border border-primary rounded px-1 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full"
      >
        {options?.map(o => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
      </select>
    );
  }

  if (type === 'date') {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="date"
          value={val}
          onChange={e => setVal(e?.target?.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="text-xs border border-primary rounded px-1 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {saving && <Icon name="Loader2" size={11} className="animate-spin text-muted-foreground" />}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        ref={inputRef}
        type="text"
        value={val}
        onChange={e => setVal(e?.target?.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="text-xs border border-primary rounded px-1 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full min-w-[80px]"
      />
      {saving && <Icon name="Loader2" size={11} className="animate-spin text-muted-foreground" />}
    </div>
  );
};

// ─── Resize Handle ────────────────────────────────────────────────────────────

const ResizeHandle = ({ onResize }) => {
  const dragging = useRef(false);
  const startX = useRef(0);

  const onMouseDown = (e) => {
    e?.preventDefault();
    dragging.current = true;
    startX.current = e?.clientX;

    const onMouseMove = (ev) => {
      if (!dragging?.current) return;
      const delta = ev?.clientX - startX?.current;
      startX.current = ev?.clientX;
      onResize(delta);
    };
    const onMouseUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      onMouseDown={onMouseDown}
      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize flex items-center justify-center group z-10"
      title="Drag to resize column"
    >
      <div className="w-0.5 h-4 bg-border group-hover:bg-primary transition-colors rounded-full" />
    </div>
  );
};

// ─── Timeline View ────────────────────────────────────────────────────────────

const TIMELINE_DEFAULT_COLS = [
  { key: 'order', label: 'Order', width: 80, minWidth: 60 },
  { key: 'name', label: 'Name', width: 280, minWidth: 150 },
  { key: 'status', label: 'Status', width: 140, minWidth: 100 },
  { key: 'startDate', label: 'Start Date', width: 120, minWidth: 90 },
  { key: 'endDate', label: 'End Date', width: 120, minWidth: 90 },
  { key: 'type', label: 'Type', width: 110, minWidth: 80 },
];

const PortfolioTimelineView = ({ projectsData, filters, sort, onUpdateDeliverable, onExpandAll, onCollapseAll }) => {
  const [collapsedProjects, setCollapsedProjects] = useState(() => {
    const init = {};
    projectsData?.forEach(pd => { init[pd?.project?.id] = true; });
    return init;
  });
  const [collapsedPhases, setCollapsedPhases] = useState({});
  const [colWidths, setColWidths] = useState(TIMELINE_DEFAULT_COLS?.map(c => c?.width));

  const totalWidth = colWidths?.reduce((a, b) => a + b, 0);

  const handleColResize = (colIdx, delta) => {
    setColWidths(prev => {
      const next = [...prev];
      const minW = TIMELINE_DEFAULT_COLS?.[colIdx]?.minWidth;
      next[colIdx] = Math.max(minW, next?.[colIdx] + delta);
      return next;
    });
  };

  const toggleProject = (id) => setCollapsedProjects(p => ({ ...p, [id]: !p?.[id] }));
  const togglePhase = (key) => setCollapsedPhases(p => ({ ...p, [key]: !p?.[key] }));

  const expandAll = useCallback(() => {
    const collapsed = {};
    projectsData?.forEach(pd => { collapsed[pd?.project?.id] = false; });
    setCollapsedProjects(collapsed);
    setCollapsedPhases({});
  }, [projectsData]);

  const collapseAll = useCallback(() => {
    const collapsed = {};
    projectsData?.forEach(pd => { collapsed[pd?.project?.id] = true; });
    setCollapsedProjects(collapsed);
  }, [projectsData]);

  useEffect(() => {
    if (onExpandAll) onExpandAll.current = expandAll;
    if (onCollapseAll) onCollapseAll.current = collapseAll;
  }, [expandAll, collapseAll, onExpandAll, onCollapseAll]);

  const STATUS_OPTIONS = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'in-progress', label: 'In Progress (alt)' },
    { value: 'completed', label: 'Completed' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'pending', label: 'Pending' },
  ];

  const filteredProjects = useMemo(() => {
    return projectsData?.map(pd => {
      let items = pd?.deliverables || [];

      if (filters?.status) items = items?.filter(d => (d?.status || '')?.toLowerCase()?.replace(/[\s-]/g, '_') === filters?.status);
      if (filters?.type) items = items?.filter(d => (d?.rowType || 'deliverable') === filters?.type);
      if (filters?.phase) items = items?.filter(d => d?.phaseId === filters?.phase || (pd?.phases || [])?.find(p => p?.id === d?.phaseId)?.name === filters?.phase);
      if (filters?.search) {
        const q = filters?.search?.toLowerCase();
        items = items?.filter(d => d?.name?.toLowerCase()?.includes(q));
      }

      const sortFn = (a, b) => {
        switch (sort) {
          case 'name_asc': return (a?.name || '')?.localeCompare(b?.name || '');
          case 'name_desc': return (b?.name || '')?.localeCompare(a?.name || '');
          case 'start_asc': return (a?.startDate || '')?.localeCompare(b?.startDate || '');
          case 'start_desc': return (b?.startDate || '')?.localeCompare(a?.startDate || '');
          case 'end_asc': return (a?.endDate || '')?.localeCompare(b?.endDate || '');
          case 'end_desc': return (b?.endDate || '')?.localeCompare(a?.endDate || '');
          default: return 0;
        }
      };
      items = [...items]?.sort(sortFn);

      return { ...pd, filteredDeliverables: items };
    })?.filter(pd => pd?.filteredDeliverables?.length > 0 || !filters?.status && !filters?.type && !filters?.search);
  }, [projectsData, filters, sort]);

  const groupByPhase = (deliverables, phases) => {
    const phaseMap = {};
    for (const p of (phases || [])) phaseMap[p.id] = p;
    const groups = {};
    for (const d of deliverables) {
      const phaseId = d?.phaseId || '__unphased__';
      if (!groups?.[phaseId]) groups[phaseId] = { phase: phaseMap?.[phaseId] || null, items: [] };
      groups?.[phaseId]?.items?.push(d);
    }
    return Object.values(groups);
  };

  const renderRow = (item, projectId, indent = 0) => {
    const sc = getStatusConfig(item?.status);
    const rtc = ROW_TYPE_CONFIG?.[item?.rowType || 'deliverable'] || ROW_TYPE_CONFIG?.deliverable;
    const indentPx = indent * 20;

    const formatOrderDisplay = (customOrder) => {
      if (customOrder == null) return '—';
      const n = Number(customOrder);
      if (Number.isInteger(n)) return String(n);
      const parts = n?.toFixed(10)?.replace(/0+$/, '')?.split('.');
      const intPart = parts?.[0];
      const decPart = parts?.[1] || '';
      if (decPart?.length >= 2) return `${intPart}.${decPart?.[0]}.${decPart?.slice(1)}`;
      return `${intPart}.${decPart}`;
    };

    return (
      <div key={item?.id} className="flex items-center hover:bg-muted/30 border-b border-border/40 text-xs" style={{ minWidth: colWidths?.reduce((a, b) => a + b, 0) }}>
        {/* Order col */}
        <div className="flex items-center px-2 py-1.5 flex-shrink-0 overflow-hidden" style={{ width: colWidths?.[0] }}>
          <EditableCell
            value={formatOrderDisplay(item?.customOrder)}
            onSave={async (v) => {
              const parsed = parseFloat(v);
              if (!isNaN(parsed)) await onUpdateDeliverable(item?.id, { customOrder: parsed });
            }}
            className="text-xs font-mono text-muted-foreground"
            renderDisplay={(v) => <span>{v}</span>}
          />
        </div>
        {/* Name col */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 overflow-hidden flex-shrink-0" style={{ width: colWidths?.[1], paddingLeft: 12 + indentPx }}>
          {indent > 0 && <span className="text-muted-foreground flex-shrink-0">{'└'}</span>}
          <Icon name={rtc?.icon} size={12} className="flex-shrink-0 text-muted-foreground" />
          <EditableCell
            value={item?.name}
            onSave={async (v) => onUpdateDeliverable(item?.id, { name: v })}
            className="truncate font-medium text-foreground"
          />
        </div>
        {/* Status col */}
        <div className="flex items-center px-2 py-1.5 flex-shrink-0 overflow-hidden" style={{ width: colWidths?.[2] }}>
          <EditableCell
            value={item?.status || 'not_started'}
            type="select"
            options={STATUS_OPTIONS}
            onSave={async (v) => onUpdateDeliverable(item?.id, { status: v })}
            className="text-xs"
            renderDisplay={(v) => {
              const cfg = getStatusConfig(v);
              return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs border ${cfg?.cls}`}>{cfg?.label}</span>;
            }}
          />
        </div>
        {/* Start Date col */}
        <div className="flex items-center px-2 py-1.5 text-muted-foreground flex-shrink-0 overflow-hidden" style={{ width: colWidths?.[3] }}>
          <EditableCell
            value={item?.startDate ? item?.startDate?.slice(0, 10) : ''}
            type="date"
            onSave={async (v) => onUpdateDeliverable(item?.id, { start_date: v })}
            className="text-xs"
          />
        </div>
        {/* End Date col */}
        <div className="flex items-center px-2 py-1.5 text-muted-foreground flex-shrink-0 overflow-hidden" style={{ width: colWidths?.[4] }}>
          <EditableCell
            value={item?.endDate ? item?.endDate?.slice(0, 10) : ''}
            type="date"
            onSave={async (v) => onUpdateDeliverable(item?.id, { end_date: v })}
            className="text-xs"
          />
        </div>
        {/* Type col */}
        <div className="flex items-center px-2 py-1.5 flex-shrink-0 overflow-hidden" style={{ width: colWidths?.[5] }}>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${rtc?.badgeCls}`}>
            {rtc?.label}
          </span>
        </div>
      </div>
    );
  };

  if (filteredProjects?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Icon name="FolderOpen" size={48} className="text-muted-foreground mb-4" />
        <p className="text-lg font-semibold text-foreground">No in-progress projects found</p>
        <p className="text-sm text-muted-foreground mt-1">Projects with "In Progress" status will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredProjects?.map(pd => {
        const isCollapsed = collapsedProjects?.[pd?.project?.id];
        const groups = groupByPhase(pd?.filteredDeliverables, pd?.phases);
        const project = pd?.project;
        const progress = parseFloat(project?.progress_percentage || project?.progressPercentage || 0);

        return (
          <div key={project?.id} className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Project Header */}
            <div
              className="flex items-start justify-between px-4 py-3 bg-muted/40 border-b border-border cursor-pointer hover:bg-muted/60 transition-colors"
              onClick={() => toggleProject(project?.id)}
            >
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronDown'} size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <Icon name="Briefcase" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  {/* Row 1: Project number + name */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {project?.projectNumber && (
                      <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded flex-shrink-0">
                        #{project?.projectNumber}
                      </span>
                    )}
                    <span className="font-semibold text-foreground text-sm">{project?.projectName}</span>
                  </div>
                  {/* Row 2: Dates + Budget + Progress */}
                  <div className="flex items-center gap-4 mt-1.5 flex-wrap" onClick={e => e?.stopPropagation()}>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Icon name="Calendar" size={11} className="flex-shrink-0" />
                      <span>{formatDate(project?.start_date || project?.startDate)}</span>
                      <span>–</span>
                      <span>{formatDate(project?.end_date || project?.endDate)}</span>
                    </div>
                    {(project?.total_budget || project?.totalBudget || project?.budget) && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Icon name="DollarSign" size={11} className="flex-shrink-0" />
                        <span>{formatCurrency(project?.total_budget || project?.totalBudget || project?.budget)}</span>
                      </div>
                    )}
                    <ProjectProgressBar progress={progress} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                <span className="text-xs text-muted-foreground">{pd?.filteredDeliverables?.length} items</span>
                {(() => {
                  const sc = getStatusConfig(project?.status);
                  return (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${sc?.cls}`}>
                      {sc?.label}
                    </span>
                  );
                })()}
              </div>
            </div>
            {!isCollapsed && (
              <div className="overflow-x-auto">
                {/* Column Headers */}
                <div className="flex bg-muted/20 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide" style={{ minWidth: totalWidth }}>
                  {TIMELINE_DEFAULT_COLS?.map((col, ci) => (
                    <div
                      key={col?.key}
                      className="relative flex items-center px-3 py-2 flex-shrink-0 select-none"
                      style={{ width: colWidths?.[ci] }}
                    >
                      <span className="truncate">{col?.label}</span>
                      <ResizeHandle onResize={(delta) => handleColResize(ci, delta)} />
                    </div>
                  ))}
                </div>

                {/* Phase Groups */}
                {groups?.map((group, gi) => {
                  const phaseKey = `${project?.id}-${group?.phase?.id || '__unphased__'}`;
                  const isPhaseCollapsed = collapsedPhases?.[phaseKey];
                  const topLevel = group?.items?.filter(d => !d?.rowType || d?.rowType === 'deliverable');
                  const tasks = group?.items?.filter(d => d?.rowType === 'task');
                  const subtasks = group?.items?.filter(d => d?.rowType === 'subtask');

                  return (
                    <div key={gi}>
                      {/* Phase Row */}
                      <div
                        className="flex items-center gap-2 px-3 py-2 bg-amber-50/60 border-b border-amber-100 cursor-pointer hover:bg-amber-50 transition-colors"
                        style={{ minWidth: totalWidth }}
                        onClick={() => togglePhase(phaseKey)}
                      >
                        <Icon name={isPhaseCollapsed ? 'ChevronRight' : 'ChevronDown'} size={13} className="text-amber-600 flex-shrink-0" />
                        <Icon name="GitBranch" size={13} className="text-amber-600 flex-shrink-0" />
                        <span className="text-xs font-semibold text-amber-800">
                          {group?.phase?.name || 'Unassigned'}
                        </span>
                        <span className="text-xs text-amber-600 ml-1">({group?.items?.length})</span>
                      </div>
                      {!isPhaseCollapsed && (
                        <>
                          {topLevel?.map(d => {
                            const dTasks = tasks?.filter(t => t?.parentId === d?.id);
                            return (
                              <React.Fragment key={d?.id}>
                                {renderRow(d, project?.id, 0)}
                                {dTasks?.map(t => {
                                  const tSubs = subtasks?.filter(s => s?.parentId === t?.id);
                                  return (
                                    <React.Fragment key={t?.id}>
                                      {renderRow(t, project?.id, 1)}
                                      {tSubs?.map(s => renderRow(s, project?.id, 2))}
                                    </React.Fragment>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                          {tasks?.filter(t => !topLevel?.find(d => d?.id === t?.parentId))?.map(t => renderRow(t, project?.id, 1))}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Gantt View ───────────────────────────────────────────────────────────────

const PortfolioGanttView = ({ projectsData, filters, sort, onUpdateDeliverable, onExpandAll, onCollapseAll }) => {
  const [collapsedProjects, setCollapsedProjects] = useState(() => {
    const init = {};
    projectsData?.forEach(pd => { init[pd?.project?.id] = true; });
    return init;
  });
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [pixelsPerDay, setPixelsPerDay] = useState(22);
  const [dateEditor, setDateEditor] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(320);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const syncingRef = useRef(false);

  const toggleProject = (id) => setCollapsedProjects(p => ({ ...p, [id]: !p?.[id] }));
  const toggleExpand = (id) => setExpandedIds(prev => {
    const next = new Set(prev);
    if (next?.has(id)) next?.delete(id); else next?.add(id);
    return next;
  });

  const expandAll = useCallback(() => {
    const collapsed = {};
    projectsData?.forEach(pd => { collapsed[pd?.project?.id] = false; });
    setCollapsedProjects(collapsed);
    const ids = new Set();
    projectsData?.forEach(pd => {
      (pd?.phases || [])?.forEach(p => ids?.add(p?.id));
      (pd?.deliverables || [])?.forEach(d => ids?.add(d?.id));
    });
    setExpandedIds(ids);
  }, [projectsData]);

  const collapseAll = useCallback(() => {
    const collapsed = {};
    projectsData?.forEach(pd => { collapsed[pd?.project?.id] = true; });
    setCollapsedProjects(collapsed);
    setExpandedIds(new Set());
  }, [projectsData]);

  useEffect(() => {
    if (onExpandAll) onExpandAll.current = expandAll;
    if (onCollapseAll) onCollapseAll.current = collapseAll;
  }, [expandAll, collapseAll, onExpandAll, onCollapseAll]);

  const { minDate, maxDate, allDates } = useMemo(() => {
    const dates = [];
    projectsData?.forEach(pd => {
      (pd?.deliverables || [])?.forEach(d => {
        if (d?.startDate) dates?.push(parseLocalDate(d?.startDate));
        if (d?.endDate) dates?.push(parseLocalDate(d?.endDate));
      });
    });
    if (dates?.length === 0) {
      const today = new Date();
      return { minDate: startOfMonth(today), maxDate: endOfMonth(addDays(today, 90)), allDates: [] };
    }
    const min = startOfMonth(new Date(Math.min(...dates)));
    const max = endOfMonth(new Date(Math.max(...dates)));
    const all = [];
    let cur = new Date(min);
    while (cur <= max) { all?.push(new Date(cur)); cur = addDays(cur, 1); }
    return { minDate: min, maxDate: max, allDates: all };
  }, [projectsData]);

  const totalWidth = allDates?.length * pixelsPerDay;

  const monthHeaders = useMemo(() => {
    const months = [];
    let cur = new Date(minDate);
    while (cur <= maxDate) {
      const start = new Date(cur);
      const end = endOfMonth(new Date(cur));
      const days = Math.min(differenceInDays(end, start) + 1, allDates?.length - differenceInDays(start, minDate));
      months?.push({ label: format(start, 'MMM yyyy'), days: Math.max(days, 1) });
      cur = addDays(endOfMonth(cur), 1);
    }
    return months;
  }, [minDate, maxDate, allDates]);

  const getBarStyle = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    const left = Math.max(0, differenceInDays(start, minDate)) * pixelsPerDay;
    const width = Math.max((differenceInDays(end, start) + 1) * pixelsPerDay, pixelsPerDay);
    return { left, width };
  };

  const handleBarClick = (e, row) => {
    e?.stopPropagation();
    const rect = e?.currentTarget?.getBoundingClientRect();
    setDateEditor({ row, x: rect?.left, y: rect?.bottom + 4 });
  };

  const handleSaveDate = async (startDate, endDate) => {
    if (!dateEditor?.row) return;
    setSavingId(dateEditor?.row?.id);
    await onUpdateDeliverable(dateEditor?.row?.id, { start_date: startDate, end_date: endDate });
    setSavingId(null);
    setDateEditor(null);
  };

  const filteredProjects = useMemo(() => {
    return projectsData?.map(pd => {
      let items = pd?.deliverables || [];
      if (filters?.status) items = items?.filter(d => (d?.status || '')?.toLowerCase()?.replace(/[\s-]/g, '_') === filters?.status);
      if (filters?.type) items = items?.filter(d => (d?.rowType || 'deliverable') === filters?.type);
      if (filters?.search) {
        const q = filters?.search?.toLowerCase();
        items = items?.filter(d => d?.name?.toLowerCase()?.includes(q));
      }
      return { ...pd, filteredDeliverables: items };
    });
  }, [projectsData, filters]);

  const buildRows = (pd) => {
    const rows = [];
    const deliverables = pd?.filteredDeliverables?.filter(d => !d?.rowType || d?.rowType === 'deliverable');
    const tasks = pd?.filteredDeliverables?.filter(d => d?.rowType === 'task');
    const subtasks = pd?.filteredDeliverables?.filter(d => d?.rowType === 'subtask');
    const phases = pd?.phases || [];

    const addDel = (del, level) => {
      const delTasks = tasks?.filter(t => t?.parentId === del?.id);
      rows?.push({ ...del, _level: level, _hasChildren: delTasks?.length > 0 });
      if (expandedIds?.has(del?.id)) {
        delTasks?.forEach(t => {
          const tSubs = subtasks?.filter(s => s?.parentId === t?.id);
          rows?.push({ ...t, _level: level + 1, _hasChildren: tSubs?.length > 0 });
          if (expandedIds?.has(t?.id)) {
            tSubs?.forEach(s => rows?.push({ ...s, _level: level + 2, _hasChildren: false }));
          }
        });
      }
    };

    if (phases?.length > 0) {
      phases?.forEach(phase => {
        const phaseDels = deliverables?.filter(d => d?.phaseId === phase?.id);
        rows?.push({ ...phase, rowType: 'phase', _isPhase: true, _level: 0, _hasChildren: phaseDels?.length > 0 });
        if (expandedIds?.has(phase?.id)) {
          phaseDels?.forEach(d => addDel(d, 1));
        }
      });
      const unphased = deliverables?.filter(d => !d?.phaseId || !phases?.find(p => p?.id === d?.phaseId));
      if (unphased?.length > 0) {
        const upId = `__unphased_${pd?.project?.id}__`;
        rows?.push({ id: upId, name: 'Unassigned', rowType: 'phase', _isPhase: true, _level: 0, _hasChildren: true });
        if (expandedIds?.has(upId)) unphased?.forEach(d => addDel(d, 1));
      }
    } else {
      deliverables?.forEach(d => addDel(d, 0));
    }
    return rows;
  };

  const ROW_H = 32;

  const renderGanttRow = (row, projectId) => {
    const barStyle = getBarStyle(row?.startDate, row?.endDate);
    const rtc = ROW_TYPE_CONFIG?.[row?.rowType || 'deliverable'] || ROW_TYPE_CONFIG?.deliverable;
    const indentPx = row?._level * 16;

    return (
      <div key={row?.id} className="flex border-b border-border/40 hover:bg-muted/20" style={{ height: ROW_H }}>
        {/* Left: name */}
        <div
          className="flex-shrink-0 flex items-center gap-1 px-2 border-r border-border/40 overflow-hidden"
          style={{ width: leftPanelWidth, paddingLeft: 8 + indentPx }}
        >
          {row?._hasChildren ? (
            <button onClick={() => toggleExpand(row?.id)} className="flex-shrink-0 text-muted-foreground hover:text-foreground">
              <Icon name={expandedIds?.has(row?.id) ? 'ChevronDown' : 'ChevronRight'} size={12} />
            </button>
          ) : <span className="w-3 flex-shrink-0" />}
          <Icon name={rtc?.icon} size={11} className={`flex-shrink-0 ${row?._isPhase ? 'text-amber-600' : 'text-muted-foreground'}`} />
          <span className={`text-xs truncate ${row?._isPhase ? 'font-semibold text-foreground' : 'text-foreground'}`}>{row?.name}</span>
        </div>
        {/* Right: bar */}
        <div className="flex-1 relative overflow-hidden" style={{ minWidth: totalWidth }}>
          {barStyle && !row?._isPhase && (
            <div
              className={`absolute top-1/2 -translate-y-1/2 rounded cursor-pointer hover:opacity-80 transition-opacity flex items-center px-1.5 overflow-hidden ${rtc?.barColor} ${savingId === row?.id ? 'opacity-50' : ''}`}
              style={{ left: barStyle?.left, width: barStyle?.width, height: 18 }}
              onClick={(e) => handleBarClick(e, row)}
              title={`${row?.name} — click to edit dates`}
            >
              <span className="text-white text-[10px] truncate font-medium">{row?.name}</span>
            </div>
          )}
          {barStyle && row?._isPhase && (
            <div
              className="absolute top-1/2 -translate-y-1/2 rounded-sm bg-amber-200 border border-amber-400"
              style={{ left: barStyle?.left, width: barStyle?.width, height: 8 }}
            />
          )}
        </div>
      </div>
    );
  };

  if (filteredProjects?.every(pd => pd?.filteredDeliverables?.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Icon name="BarChart3" size={48} className="text-muted-foreground mb-4" />
        <p className="text-lg font-semibold text-foreground">No items to display</p>
        <p className="text-sm text-muted-foreground mt-1">Adjust filters to see Gantt data.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Gantt header */}
        <div className="flex border-b border-border bg-muted/30">
          {/* Left panel header with resize handle */}
          <div className="relative flex-shrink-0 border-r border-border px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide select-none" style={{ width: leftPanelWidth }}>
            <span>Name</span>
            <ResizeHandle onResize={(delta) => setLeftPanelWidth(w => Math.max(160, w + delta))} />
          </div>
          <div className="flex-1 overflow-x-auto">
            <div style={{ minWidth: totalWidth }}>
              {/* Month row */}
              <div className="flex">
                {monthHeaders?.map((m, i) => (
                  <div
                    key={i}
                    className="border-r border-border/50 text-xs font-semibold text-muted-foreground px-2 py-1 flex-shrink-0"
                    style={{ width: m?.days * pixelsPerDay }}
                  >
                    {m?.label}
                  </div>
                ))}
              </div>
              {/* Day row */}
              <div className="flex border-t border-border/30">
                {allDates?.map((d, i) => (
                  <div
                    key={i}
                    className={`flex-shrink-0 text-center border-r border-border/20 text-[9px] text-muted-foreground/60 ${d?.getDay() === 0 || d?.getDay() === 6 ? 'bg-muted/30' : ''}`}
                    style={{ width: pixelsPerDay, lineHeight: '16px' }}
                  >
                    {d?.getDate() === 1 || i === 0 ? d?.getDate() : (d?.getDate() % 5 === 0 ? d?.getDate() : '')}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Zoom controls */}
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/10">
          <span className="text-xs text-muted-foreground">Zoom:</span>
          {[14, 22, 32, 48]?.map(ppd => (
            <button
              key={ppd}
              onClick={() => setPixelsPerDay(ppd)}
              className={`text-xs px-2 py-0.5 rounded border transition-colors ${pixelsPerDay === ppd ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
            >
              {ppd === 14 ? 'S' : ppd === 22 ? 'M' : ppd === 32 ? 'L' : 'XL'}
            </button>
          ))}
        </div>
        {/* Rows */}
        <div className="overflow-auto max-h-[600px]">
          {filteredProjects?.map(pd => {
            const isCollapsed = collapsedProjects?.[pd?.project?.id];
            const rows = buildRows(pd);
            const project = pd?.project;
            const progress = parseFloat(project?.progress_percentage || project?.progressPercentage || 0);

            return (
              <div key={project?.id}>
                {/* Project header row */}
                <div
                  className="flex items-center gap-0 border-b border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors bg-primary/5"
                  onClick={() => toggleProject(project?.id)}
                >
                  {/* Left label panel */}
                  <div className="flex-shrink-0 flex items-start gap-2 px-3 py-2 border-r border-primary/20" style={{ width: leftPanelWidth }}>
                    <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronDown'} size={14} className="text-primary flex-shrink-0 mt-0.5" />
                    <Icon name="Briefcase" size={14} className="text-primary flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1 overflow-hidden">
                      {project?.projectNumber && (
                        <div className="text-[10px] font-mono text-muted-foreground mb-0.5">
                          #{project?.projectNumber}
                        </div>
                      )}
                      <div className="text-xs font-semibold text-foreground leading-tight break-words">{project?.projectName}</div>
                    </div>
                  </div>
                  {/* Right: project meta info spanning gantt area */}
                  <div className="flex-1 flex items-center gap-4 px-3 py-2 overflow-hidden" onClick={e => e?.stopPropagation()}>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                      <Icon name="Calendar" size={11} />
                      <span>{formatDate(project?.start_date || project?.startDate)}</span>
                      <span>–</span>
                      <span>{formatDate(project?.end_date || project?.endDate)}</span>
                    </div>
                    {(project?.total_budget || project?.totalBudget || project?.budget) && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                        <Icon name="DollarSign" size={11} />
                        <span>{formatCurrency(project?.total_budget || project?.totalBudget || project?.budget)}</span>
                      </div>
                    )}
                    <ProjectProgressBar progress={progress} />
                    <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">{rows?.length} rows</span>
                  </div>
                </div>
                {!isCollapsed && rows?.map(row => renderGanttRow(row, project?.id))}
              </div>
            );
          })}
        </div>
        {/* Date editor popover */}
        {dateEditor && (
          <DateEditPopover
            row={dateEditor?.row}
            x={dateEditor?.x}
            y={dateEditor?.y}
            saving={savingId === dateEditor?.row?.id}
            onSave={handleSaveDate}
            onClose={() => setDateEditor(null)}
          />
        )}
      </div>
    </div>
  );
};

// ─── Date Edit Popover ────────────────────────────────────────────────────────

const DateEditPopover = ({ row, x, y, onSave, onClose, saving }) => {
  const [startDate, setStartDate] = useState(row?.startDate ? row?.startDate?.slice(0, 10) : '');
  const [endDate, setEndDate] = useState(row?.endDate ? row?.endDate?.slice(0, 10) : '');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref?.current && !ref?.current?.contains(e?.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const style = {
    position: 'fixed',
    zIndex: 9999,
    left: Math.min(x, window.innerWidth - 260),
    top: Math.min(y, window.innerHeight - 200),
  };

  return (
    <div ref={ref} className="bg-popover border border-border rounded-lg shadow-xl p-3 w-56" style={style}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-foreground">Edit Date Range</span>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-0.5 rounded">
          <Icon name="X" size={12} />
        </button>
      </div>
      <p className="text-xs text-muted-foreground mb-2 truncate font-medium">{row?.name}</p>
      <div className="space-y-2">
        <div>
          <label className="text-xs text-muted-foreground block mb-0.5">Start Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e?.target?.value)}
            className="w-full text-xs border border-border rounded px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-0.5">End Date</label>
          <input type="date" value={endDate} min={startDate} onChange={e => setEndDate(e?.target?.value)}
            className="w-full text-xs border border-border rounded px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => startDate && endDate && onSave(startDate, endDate)}
          disabled={saving || !startDate || !endDate}
          className="flex-1 text-xs bg-primary text-primary-foreground rounded px-2 py-1 hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-1"
        >
          {saving ? <Icon name="Loader2" size={11} className="animate-spin" /> : <Icon name="Check" size={11} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onClose} className="flex-1 text-xs border border-border rounded px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted">
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main Portfolio Page ──────────────────────────────────────────────────────

const Portfolio = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectsData, setProjectsData] = useState([]);
  const [viewMode, setViewMode] = useState('timeline');
  const [filters, setFilters] = useState({ status: '', type: '', phase: '', search: '', pm: '' });
  const [sort, setSort] = useState('start_asc');
  const [notification, setNotification] = useState(null);
  const [pmUsers, setPmUsers] = useState([]);

  const expandAllRef = useRef(null);
  const collapseAllRef = useRef(null);

  const showNotification = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: projects, error: pErr } = await projectService?.getAll();
      if (pErr) throw pErr;

      const inProgress = (projects || [])?.filter(p =>
        (p?.status || '')?.toLowerCase() === 'in progress' ||
        (p?.status || '')?.toLowerCase() === 'in_progress' ||
        (p?.status || '')?.toLowerCase() === 'bidding'
      );

      const enriched = await Promise.all(
        inProgress?.map(async (project) => {
          const { data: deliverables } = await deliverableService?.getByProject(project?.id);
          const { data: phases } = await projectService?.getPhases(project?.id);
          return { project, deliverables: deliverables || [], phases: phases || [] };
        })
      );

      setProjectsData(enriched);
    } catch (err) {
      setError(err?.message || 'Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const loadPMUsers = async () => {
      try {
        const { data } = await projectService?.getPMUsers();
        setPmUsers(data || []);
      } catch (err) {
        // silently fail
      }
    };
    loadPMUsers();
  }, []);

  const handleUpdateDeliverable = async (id, updates) => {
    try {
      // Convert camelCase customOrder to snake_case for DB
      const dbUpdates = { ...updates };
      if (dbUpdates?.customOrder !== undefined) {
        dbUpdates.custom_order = dbUpdates?.customOrder;
        delete dbUpdates?.customOrder;
      }
      const { error } = await supabase?.from('deliverables')?.update(dbUpdates)?.eq('id', id);
      if (error) throw error;
      setProjectsData(prev => prev?.map(pd => ({
        ...pd,
        deliverables: pd?.deliverables?.map(d => {
          if (d?.id !== id) return d;
          const merged = { ...d };
          if (updates?.name !== undefined) merged.name = updates?.name;
          if (updates?.start_date !== undefined) merged.startDate = updates?.start_date;
          if (updates?.end_date !== undefined) merged.endDate = updates?.end_date;
          if (updates?.status !== undefined) merged.status = updates?.status;
          if (updates?.customOrder !== undefined) merged.customOrder = updates?.customOrder;
          return merged;
        })
      })));
      showNotification('Updated successfully');
    } catch (err) {
      showNotification(err?.message || 'Failed to update', 'error');
    }
  };

  const allPhases = useMemo(() => {
    const seen = new Set();
    const result = [];
    projectsData?.forEach(pd => {
      (pd?.phases || [])?.forEach(p => {
        if (!seen?.has(p?.id)) { seen?.add(p?.id); result?.push(p); }
      });
    });
    return result;
  }, [projectsData]);

  const totalItems = useMemo(() =>
    projectsData?.reduce((sum, pd) => sum + (pd?.deliverables?.length || 0), 0),
    [projectsData]
  );

  const stats = useMemo(() => {
    const allProjects = projectsData?.map(pd => pd?.project) || [];
    const totalBudget = allProjects?.reduce((sum, p) => sum + (parseFloat(p?.total_budget || p?.totalBudget || p?.budget) || 0), 0);
    const inProgress = allProjects?.filter(p => (p?.status || '')?.toLowerCase()?.replace(/[\s-]/g, '_') === 'in_progress' || p?.status === 'In Progress')?.length || 0;
    const avgProgress = allProjects?.length > 0
      ? allProjects?.reduce((sum, p) => sum + (parseFloat(p?.progress_percentage || p?.progressPercentage || p?.completionPercentage) || 0), 0) / allProjects?.length
      : 0;
    return { totalProjects: allProjects?.length, inProgress, totalBudget, avgProgress, totalItems };
  }, [projectsData, totalItems]);

  const formatCurrencyShort = (val) => {
    if (!val && val !== 0) return '$0';
    const num = parseFloat(val);
    if (isNaN(num)) return '$0';
    if (num >= 1000000) return `$${(num / 1000000)?.toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000)?.toFixed(0)}K`;
    return `$${num?.toLocaleString()}`;
  };

  const clearFilters = () => setFilters({ status: '', type: '', phase: '', search: '', pm: '' });
  const hasActiveFilters = filters?.status || filters?.type || filters?.phase || filters?.search || filters?.pm;

  // Filter projectsData by PM at the project level
  const filteredProjectsData = useMemo(() => {
    if (!filters?.pm) return projectsData;
    return projectsData?.filter(pd => pd?.project?.assignedPmId === filters?.pm);
  }, [projectsData, filters?.pm]);

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        <div className="max-w-full px-4 sm:px-6 py-6">

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Portfolio</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                All in-progress projects with phases, deliverables, tasks and subtasks
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {projectsData?.length} project{projectsData?.length !== 1 ? 's' : ''} · {totalItems} items
              </span>
              <Button variant="outline" size="sm" onClick={loadData}>
                <Icon name="RefreshCw" size={14} className="mr-1.5" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Notification */}
          {notification && (
            <div className={`mb-4 flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border ${
              notification?.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'
            }`}>
              <Icon name={notification?.type === 'error' ? 'AlertCircle' : 'CheckCircle2'} size={15} />
              {notification?.msg}
            </div>
          )}

          {/* Stat Boxes */}
          {!loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
              <div className="bg-card rounded-lg border border-border p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="FolderKanban" size={14} className="text-accent" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground truncate">In Progress</span>
                </div>
                <p className="text-2xl font-bold text-foreground leading-none mb-1">{stats?.totalProjects}</p>
                <p className="text-xs text-muted-foreground">Active Projects</p>
              </div>
              <div className="bg-card rounded-lg border border-border p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-md bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Clock" size={14} className="text-blue-500" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground truncate">In Progress</span>
                </div>
                <p className="text-2xl font-bold text-foreground leading-none mb-1">{stats?.inProgress}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
              <div className="bg-card rounded-lg border border-border p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-md bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Package" size={14} className="text-purple-500" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground truncate">Total Items</span>
                </div>
                <p className="text-2xl font-bold text-foreground leading-none mb-1">{stats?.totalItems}</p>
                <p className="text-xs text-muted-foreground">Deliverables & Tasks</p>
              </div>
              <div className="bg-card rounded-lg border border-border p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-md bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="TrendingUp" size={14} className="text-green-500" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground truncate">Avg Progress</span>
                </div>
                <p className="text-2xl font-bold text-foreground leading-none mb-1">{stats?.avgProgress?.toFixed(0)}%</p>
                <p className="text-xs text-muted-foreground">Average Completion</p>
              </div>
              <div className="bg-card rounded-lg border border-border p-3 col-span-2 sm:col-span-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="DollarSign" size={14} className="text-accent" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground truncate">Total Value</span>
                </div>
                <p className="text-xl font-bold text-foreground leading-none mb-1">{formatCurrencyShort(stats?.totalBudget)}</p>
                <p className="text-xs text-muted-foreground">Active Budget</p>
              </div>
            </div>
          )}

          {/* Toolbar: View Toggle + Filters + Sort + Expand/Collapse */}
          <div className="flex flex-wrap items-center gap-3 mb-5 p-3 bg-card border border-border rounded-lg">
            {/* View toggle */}
            <div className="flex items-center bg-muted rounded-md p-0.5">
              <button
                onClick={() => setViewMode('timeline')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === 'timeline' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Icon name="List" size={13} />
                Timeline
              </button>
              <button
                onClick={() => setViewMode('gantt')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === 'gantt' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Icon name="BarChart3" size={13} />
                Gantt
              </button>
            </div>

            <div className="w-px h-5 bg-border" />

            {/* Search */}
            <div className="relative">
              <Icon name="Search" size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search items…"
                value={filters?.search}
                onChange={e => setFilters(f => ({ ...f, search: e?.target?.value }))}
                className="pl-7 pr-3 py-1.5 text-xs border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-44"
              />
            </div>

            {/* PM filter */}
            {pmUsers?.length > 0 && (
              <select
                value={filters?.pm}
                onChange={e => setFilters(f => ({ ...f, pm: e?.target?.value }))}
                className="text-xs border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary min-w-[140px]"
              >
                <option value="">All PMs</option>
                {pmUsers?.map(pm => (
                  <option key={pm?.id} value={pm?.id}>
                    {pm?.nickname || pm?.fullName || pm?.email}
                  </option>
                ))}
              </select>
            )}

            {/* Status filter */}
            <select
              value={filters?.status}
              onChange={e => setFilters(f => ({ ...f, status: e?.target?.value }))}
              className="text-xs border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary min-w-[130px]"
            >
              <option value="">All Statuses</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="bidding">Bidding</option>
              <option value="completed">Completed</option>
              <option value="blocked">Blocked</option>
              <option value="on_hold">On Hold</option>
            </select>

            {/* Type filter */}
            <select
              value={filters?.type}
              onChange={e => setFilters(f => ({ ...f, type: e?.target?.value }))}
              className="text-xs border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary min-w-[130px]"
            >
              <option value="">All Types</option>
              <option value="deliverable">Deliverable</option>
              <option value="task">Task</option>
              <option value="subtask">Sub-task</option>
            </select>

            {/* Phase filter */}
            {allPhases?.length > 0 && (
              <select
                value={filters?.phase}
                onChange={e => setFilters(f => ({ ...f, phase: e?.target?.value }))}
                className="text-xs border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary min-w-[130px]"
              >
                <option value="">All Phases</option>
                {allPhases?.map(p => <option key={p?.id} value={p?.id}>{p?.name}</option>)}
              </select>
            )}

            {/* Sort */}
            <select
              value={sort}
              onChange={e => setSort(e?.target?.value)}
              className="text-xs border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary min-w-[130px]"
            >
              <option value="start_asc">Start Date ↑</option>
              <option value="start_desc">Start Date ↓</option>
              <option value="end_asc">End Date ↑</option>
              <option value="end_desc">End Date ↓</option>
              <option value="name_asc">Name (A–Z)</option>
              <option value="name_desc">Name (Z–A)</option>
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1.5 hover:bg-muted transition-colors"
              >
                <Icon name="X" size={12} />
                Clear
              </button>
            )}

            <div className="w-px h-5 bg-border" />

            {/* Expand / Collapse All */}
            <button
              onClick={() => expandAllRef?.current?.()}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-border rounded-md bg-card text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="ChevronsDown" size={13} />
              Expand All
            </button>
            <button
              onClick={() => collapseAllRef?.current?.()}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-border rounded-md bg-card text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="ChevronsUp" size={13} />
              Collapse All
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading portfolio data…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Icon name="AlertCircle" size={40} className="text-red-500" />
              <p className="text-base font-semibold text-foreground">Failed to load portfolio</p>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" onClick={loadData}>
                <Icon name="RefreshCw" size={14} className="mr-1.5" />
                Try Again
              </Button>
            </div>
          ) : viewMode === 'timeline' ? (
            <PortfolioTimelineView
              projectsData={filteredProjectsData}
              filters={filters}
              sort={sort}
              onUpdateDeliverable={handleUpdateDeliverable}
              onExpandAll={expandAllRef}
              onCollapseAll={collapseAllRef}
            />
          ) : (
            <PortfolioGanttView
              projectsData={filteredProjectsData}
              filters={filters}
              sort={sort}
              onUpdateDeliverable={handleUpdateDeliverable}
              onExpandAll={expandAllRef}
              onCollapseAll={collapseAllRef}
            />
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Portfolio;
